
import { supabase } from "@/integrations/supabase/client";
import { mockSuggestedCandidates } from "@/utils/mock/suggestedCandidates";
import type { SuggestedCandidate } from "@/types/candidateSuggestion";

interface JobForMatching {
  id: string;
  title: string;
  skills: string[];
  experience_required: number | null;
  location: string | null;
  posted: string;
  company?: string;
  description?: string;
}

export async function getSuggestedCandidates(
  job: JobForMatching,
  userId: string
): Promise<SuggestedCandidate[]> {
  try {
    // Check if user is a developer
    const { data: profile } = await supabase
      .from('profiles')
      .select('developer_mode')
      .eq('id', userId)
      .single();
    
    const isDeveloper = profile?.developer_mode || false;
    
    // If user is a developer, return mock data
    if (isDeveloper) {
      console.log('Developer mode: returning mock candidate suggestions');
      return mockSuggestedCandidates;
    }

    // For non-developers, fetch real candidates from database
    const { data: candidates, error } = await supabase
      .from('candidates')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching candidates for matching:', error);
      return [];
    }

    if (!candidates || candidates.length === 0) {
      return [];
    }

    // Transform real candidates into suggested candidates with basic matching
    const suggestedCandidates: SuggestedCandidate[] = candidates.map(candidate => {
      const fullName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
      
      // Basic skill matching logic
      let skillMatchScore = 0;
      if (job.skills && candidate.skills) {
        const jobSkills = job.skills.map(s => s.toLowerCase());
        const candidateSkills = candidate.skills.map(s => s.toLowerCase());
        const matchingSkills = candidateSkills.filter(skill => 
          jobSkills.some(jobSkill => jobSkill.includes(skill) || skill.includes(jobSkill))
        );
        skillMatchScore = jobSkills.length > 0 ? (matchingSkills.length / jobSkills.length) * 100 : 0;
      }

      // Basic experience matching
      let experienceMatchScore = 50; // Default score
      if (job.experience_required && candidate.experience_years) {
        const diff = Math.abs(candidate.experience_years - job.experience_required);
        experienceMatchScore = Math.max(0, 100 - (diff * 10));
      }

      // Calculate overall match score
      const matchScore = Math.round((skillMatchScore * 0.6 + experienceMatchScore * 0.4));

      return {
        id: candidate.id,
        name: fullName || 'Unnamed Candidate',
        skills: candidate.skills || [],
        experience_years: candidate.experience_years || 0,
        location: candidate.location_city ? `${candidate.location_city}${candidate.location_state ? `, ${candidate.location_state}` : ''}` : null,
        match_score: Math.max(1, matchScore), // Ensure minimum score of 1
        match_reasons: {
          skills: candidate.skills || [],
          experience: candidate.experience_years ? `${candidate.experience_years} years` : '0 years',
          location: candidate.location_city || null
        },
        email: candidate.email,
        phone: candidate.phone,
        linkedin_url: candidate.linkedin_url,
        status: candidate.status || 'new'
      };
    });

    // Sort by match score descending
    return suggestedCandidates.sort((a, b) => b.match_score - a.match_score);

  } catch (error) {
    console.error('Error in getSuggestedCandidates:', error);
    return [];
  }
}
